import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, catchError, filter, switchMap, take, throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';

function isRefreshOrAuthRequest(url: string): boolean {
  return url.includes('refresh/token') || url.includes('/login') || url.includes('/oauth/token');
}

function cloneWithToken(req: HttpRequest<any>, token: string): HttpRequest<any> {
  return req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
}

function handle401(
  req: HttpRequest<any>,
  next: HttpHandlerFn,
  authService: AuthService
): Observable<HttpEvent<any>> {
  if (!authService.isRefreshing) {
    authService.isRefreshing = true;
    authService.refreshTokenSubject.next(null);

    return authService.doRefreshToken().pipe(
      switchMap((newToken) => {
        authService.isRefreshing = false;
        if (newToken) {
          authService.refreshTokenSubject.next(newToken);
          return next(cloneWithToken(req, newToken));
        }
        authService.clear();
        return throwError(() => new Error('Session expirée, veuillez vous reconnecter.'));
      }),
      catchError((err) => {
        authService.isRefreshing = false;
        authService.clear();
        return throwError(() => err);
      })
    );
  }

  // Une autre requête est déjà en train de rafraîchir — attendre le nouveau token.
  return authService.refreshTokenSubject.pipe(
    filter((token): token is string => token !== null),
    take(1),
    switchMap((token) => next(cloneWithToken(req, token)))
  );
}

export const AuthInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401 && !isRefreshOrAuthRequest(req.url)) {
        // Visiteur anonyme (aucune session) : un 401 sur une page PUBLIQUE
        // (aperçu devis/facture, bootstrap shops…) ne doit PAS déclencher
        // clear()+redirect vers /login. On laisse l'erreur remonter.
        if (!authService.hasSession()) {
          return throwError(() => error);
        }
        return handle401(req, next, authService);
      }
      if ((error.status === 401 || error.status === 400) && isRefreshOrAuthRequest(req.url)) {
        authService.clear();
      }
      return throwError(() => error);
    })
  );
};
